import React, { useEffect } from "react";
import { useParams, useNavigate, Navigate, Link } from "react-router-dom";
import { CheckCircle2, Clock, MapPin, Car, ShieldCheck, Phone, ArrowRight, Plane } from "lucide-react";
import { BRAND } from "../mock";
import { resolvePage, buildPage, canonicalOf, otherCities, variantKeywords, cityName } from "../data/seoData";

const SITE = "https://airporttaxitransfers.ch";

const CT = {
  de: {
    kicker: "Flughafen Zürich (ZRH) · Fixpreis",
    intro: (d) => `${d.keyword}: Zuverlässiger Chauffeurservice zwischen dem Flughafen Zürich (ZRH) und ${d.cityDisplay}. Die Strecke ist rund ${d.km} km lang und dauert ca. ${d.minutes} Minuten. Sie fahren zum garantierten Fixpreis ab CHF ${d.price} – ohne versteckte Kosten, 24 Stunden am Tag, an 365 Tagen im Jahr.`,
    factsT: "Ihre Fahrt auf einen Blick",
    f: ["Distanz", "Fahrzeit", "Fixpreis ab", "Verfügbar"],
    why: "Warum Airport Taxi Transfers?",
    bullets: ["Fixpreis ohne Überraschungen – online buchbar", "Kostenlose Flugverfolgung & Wartezeit", "Moderne Limousinen, Vans & Business-Fahrzeuge", "Professionelle, ortskundige Chauffeure"],
    body: (d) => `Ob geschäftlich oder privat: Für die Fahrt ${d.cityDisplay} ⇄ Flughafen Zürich stehen wir rund um die Uhr bereit. Ihr Chauffeur erwartet Sie mit Namensschild in der Ankunftshalle und hilft Ihnen mit dem Gepäck. Kindersitze, zusätzliche Stopps und Rückfahrten sind jederzeit möglich.`,
    faqT: "Häufige Fragen",
    faq: (d) => [
      [`Was kostet ein Taxi vom Flughafen Zürich nach ${d.cityDisplay}?`, `Der Fixpreis für die Strecke Flughafen Zürich – ${d.cityDisplay} (ca. ${d.km} km) beginnt bei CHF ${d.price} für eine Standardlimousine. Der Endpreis hängt von Fahrzeugklasse und Extras ab.`],
      [`Wie lange dauert die Fahrt nach ${d.cityDisplay}?`, `Die reine Fahrzeit beträgt normalerweise etwa ${d.minutes} Minuten, abhängig von Verkehr und Wetter.`],
      ["Verfolgen Sie meinen Flug?", "Ja. Wir überwachen Ihre Flugnummer und passen die Abholzeit automatisch an – Wartezeit ist inklusive."],
      ["Kann ich bar oder online bezahlen?", "Beides. Sie zahlen online per Karte, Apple/Google Pay oder bar direkt beim Chauffeur."],
    ],
    cta: "Jetzt Fixpreis berechnen",
    call: "Anrufen",
    variantsT: "Beliebte Suchbegriffe",
    moreT: "Weitere Ziele ab Flughafen Zürich",
  },
  en: {
    kicker: "Zurich Airport (ZRH) · Fixed price",
    intro: (d) => `${d.keyword}: reliable chauffeur service between Zurich Airport (ZRH) and ${d.cityDisplay}. The route is about ${d.km} km and takes roughly ${d.minutes} minutes. You travel at a guaranteed fixed price from CHF ${d.price} – no hidden costs, 24 hours a day, 365 days a year.`,
    factsT: "Your ride at a glance",
    f: ["Distance", "Drive time", "Fixed price from", "Available"],
    why: "Why Airport Taxi Transfers?",
    bullets: ["Fixed price, no surprises – book online", "Free flight tracking & waiting time", "Modern sedans, vans & business cars", "Professional, local chauffeurs"],
    body: (d) => `Whether for business or leisure, we are ready around the clock for your ${d.cityDisplay} ⇄ Zurich Airport transfer. Your chauffeur greets you with a name sign in the arrivals hall and helps with your luggage. Child seats, extra stops and return trips are available anytime.`,
    faqT: "Frequently asked questions",
    faq: (d) => [
      [`How much is a taxi from Zurich Airport to ${d.cityDisplay}?`, `The fixed price for Zurich Airport – ${d.cityDisplay} (approx. ${d.km} km) starts at CHF ${d.price} for a standard sedan. The final price depends on vehicle class and extras.`],
      [`How long is the drive to ${d.cityDisplay}?`, `The drive normally takes about ${d.minutes} minutes, depending on traffic and weather.`],
      ["Do you track my flight?", "Yes. We monitor your flight number and adjust the pickup time automatically – waiting time is included."],
      ["Can I pay cash or online?", "Both. Pay online by card, Apple/Google Pay, or cash directly to the chauffeur."],
    ],
    cta: "Calculate fixed price",
    call: "Call now",
    variantsT: "Popular searches",
    moreT: "More destinations from Zurich Airport",
  },
  fr: {
    kicker: "Aéroport de Zurich (ZRH) · Prix fixe",
    intro: (d) => `${d.keyword} : service de chauffeur fiable entre l'aéroport de Zurich (ZRH) et ${d.cityDisplay}. Le trajet fait environ ${d.km} km et dure à peu près ${d.minutes} minutes. Vous voyagez à un prix fixe garanti dès CHF ${d.price} – sans frais cachés, 24h/24, 365 jours par an.`,
    factsT: "Votre trajet en un coup d'œil",
    f: ["Distance", "Durée", "Prix fixe dès", "Disponible"],
    why: "Pourquoi Airport Taxi Transfers ?",
    bullets: ["Prix fixe, sans surprise – réservable en ligne", "Suivi des vols & temps d'attente gratuits", "Berlines, vans et véhicules d'affaires modernes", "Chauffeurs professionnels et locaux"],
    body: (d) => `Pour affaires ou loisirs, nous sommes disponibles 24h/24 pour votre transfert ${d.cityDisplay} ⇄ aéroport de Zurich. Votre chauffeur vous accueille avec une pancarte nominative dans le hall d'arrivée et vous aide avec vos bagages. Sièges enfants, arrêts supplémentaires et allers-retours sont possibles à tout moment.`,
    faqT: "Questions fréquentes",
    faq: (d) => [
      [`Combien coûte un taxi de l'aéroport de Zurich à ${d.cityDisplay} ?`, `Le prix fixe pour aéroport de Zurich – ${d.cityDisplay} (env. ${d.km} km) débute à CHF ${d.price} pour une berline standard.`],
      [`Combien de temps dure le trajet vers ${d.cityDisplay} ?`, `Le trajet dure normalement environ ${d.minutes} minutes, selon le trafic et la météo.`],
      ["Suivez-vous mon vol ?", "Oui. Nous surveillons votre numéro de vol et adaptons l'heure de prise en charge – le temps d'attente est inclus."],
      ["Puis-je payer en espèces ou en ligne ?", "Les deux. Payez en ligne par carte, Apple/Google Pay, ou en espèces au chauffeur."],
    ],
    cta: "Calculer le prix fixe",
    call: "Appeler",
    variantsT: "Recherches populaires",
    moreT: "Autres destinations depuis l'aéroport de Zurich",
  },
  it: {
    kicker: "Aeroporto di Zurigo (ZRH) · Prezzo fisso",
    intro: (d) => `${d.keyword}: servizio di autista affidabile tra l'aeroporto di Zurigo (ZRH) e ${d.cityDisplay}. Il percorso è di circa ${d.km} km e dura circa ${d.minutes} minuti. Viaggi a un prezzo fisso garantito da CHF ${d.price} – senza costi nascosti, 24 ore su 24, 365 giorni all'anno.`,
    factsT: "Il tuo viaggio in sintesi",
    f: ["Distanza", "Durata", "Prezzo fisso da", "Disponibile"],
    why: "Perché Airport Taxi Transfers?",
    bullets: ["Prezzo fisso, nessuna sorpresa – prenotabile online", "Monitoraggio voli e tempo di attesa gratuiti", "Berline, van e auto business moderne", "Autisti professionali e locali"],
    body: (d) => `Per affari o piacere, siamo disponibili 24 ore su 24 per il tuo transfer ${d.cityDisplay} ⇄ aeroporto di Zurigo. Il tuo autista ti accoglie con un cartello nominativo nella hall arrivi e ti aiuta con i bagagli. Seggiolini, fermate extra e viaggi di ritorno sono sempre possibili.`,
    faqT: "Domande frequenti",
    faq: (d) => [
      [`Quanto costa un taxi dall'aeroporto di Zurigo a ${d.cityDisplay}?`, `Il prezzo fisso per aeroporto di Zurigo – ${d.cityDisplay} (circa ${d.km} km) parte da CHF ${d.price} per una berlina standard.`],
      [`Quanto dura il viaggio verso ${d.cityDisplay}?`, `Il viaggio dura normalmente circa ${d.minutes} minuti, a seconda del traffico e del meteo.`],
      ["Monitorate il mio volo?", "Sì. Monitoriamo il numero di volo e adattiamo l'orario di ritiro – il tempo di attesa è incluso."],
      ["Posso pagare in contanti o online?", "Entrambi. Paga online con carta, Apple/Google Pay o in contanti all'autista."],
    ],
    cta: "Calcola il prezzo fisso",
    call: "Chiama ora",
    variantsT: "Ricerche popolari",
    moreT: "Altre destinazioni dall'aeroporto di Zurigo",
  },
};

const upsertMeta = (attr, key, content) => {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
  el.setAttribute("content", content);
};
const upsertLink = (rel, href, hreflang) => {
  const sel = hreflang ? `link[rel="alternate"][hreflang="${hreflang}"]` : `link[rel="${rel}"]`;
  let el = document.head.querySelector(sel);
  if (!el) { el = document.createElement("link"); el.setAttribute("rel", rel); if (hreflang) el.setAttribute("hreflang", hreflang); document.head.appendChild(el); }
  el.setAttribute("href", href);
};

const Landing = () => {
  const { lang, slug } = useParams();
  const navigate = useNavigate();
  const data = ["de", "en", "fr", "it"].includes(lang) ? resolvePage(lang, slug) : null;
  const canon = data ? canonicalOf(lang, data.city) : null;

  useEffect(() => {
    if (!data) return;
    const t = CT[lang];
    const canonUrl = `${SITE}/${lang}/${canon.slug}`;
    const desc = t.intro(canon).slice(0, 300);
    document.title = `${canon.keyword} | ${BRAND.name}`;
    upsertMeta("name", "description", desc);
    upsertLink("canonical", canonUrl);
    upsertMeta("property", "og:title", canon.keyword);
    upsertMeta("property", "og:description", desc);
    upsertMeta("property", "og:image", canon.image);
    upsertMeta("property", "og:url", canonUrl);
    ["de", "en", "fr", "it"].forEach((l) => { const pg = buildPage(l, "transfer", data.city); upsertLink("alternate", `${SITE}/${l}/${pg.slug}`, l); });
    const dePg = buildPage("de", "transfer", data.city);
    upsertLink("alternate", `${SITE}/de/${dePg.slug}`, "x-default");
    // JSON-LD
    const faq = t.faq(data);
    const ld = {
      "@context": "https://schema.org",
      "@type": "Service",
      name: canon.keyword,
      areaServed: data.cityDisplay,
      provider: { "@type": "LocalBusiness", name: BRAND.name, telephone: BRAND.phone, url: SITE },
      offers: { "@type": "Offer", price: data.price, priceCurrency: "CHF" },
    };
    const faqLd = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faq.map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })),
    };
    let s1 = document.getElementById("seo-ld"); if (!s1) { s1 = document.createElement("script"); s1.type = "application/ld+json"; s1.id = "seo-ld"; document.head.appendChild(s1); }
    s1.textContent = JSON.stringify([ld, faqLd]);
    window.scrollTo(0, 0);
    return () => { const el = document.getElementById("seo-ld"); if (el) el.textContent = "[]"; };
  }, [lang, slug, data]);

  if (!data) return <Navigate to="/" replace />;
  const t = CT[lang];
  const facts = [
    { i: MapPin, l: t.f[0], v: `${data.km} km` },
    { i: Clock, l: t.f[1], v: `~${data.minutes} min` },
    { i: Car, l: t.f[2], v: `CHF ${data.price}` },
    { i: ShieldCheck, l: t.f[3], v: "24/7" },
  ];
  const goBook = () => navigate(`/booking?from=${encodeURIComponent("Flughafen Zürich (ZRH)")}&to=${encodeURIComponent(data.cityDisplay + ", Schweiz")}&date=&time=`);

  return (
    <div className="bg-white" data-testid="seo-landing">
      <section className="relative pt-28 pb-14 bg-[#0B1533] overflow-hidden">
        <img src={data.image} alt={data.keyword} className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B1533]/70 to-[#0B1533]" />
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6">
          <p className="font-body text-xs uppercase tracking-[0.28em] text-[#C9A24B] mb-3">{t.kicker}</p>
          <h1 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-white leading-tight" data-testid="seo-h1">{canon.keyword}</h1>
          <p className="font-body text-white/75 text-sm sm:text-base mt-4 max-w-2xl leading-relaxed">{t.intro(canon)}</p>
          <button onClick={goBook} data-testid="seo-cta-top" className="mt-7 inline-flex items-center gap-2 bg-[#2B4BFF] text-white font-body font-semibold rounded-2xl px-7 py-3.5 hover:bg-[#1E38D8] transition-colors">{t.cta} <ArrowRight size={18} /></button>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 sm:px-6 -mt-8 relative z-20">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {facts.map((f, i) => (
            <div key={i} className="bg-white rounded-2xl border border-[#E9E5DE] p-4 elev-soft text-center">
              <f.i size={20} className="text-[#2B4BFF] mx-auto mb-2" />
              <p className="font-body text-xs text-[#5A607A]">{f.l}</p>
              <p className="font-heading font-bold text-[#0B1533]">{f.v}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
        <h2 className="font-heading font-bold text-2xl text-[#0B1533] mb-5">{t.why}</h2>
        <div className="grid sm:grid-cols-2 gap-3 mb-8">
          {t.bullets.map((b, i) => (
            <div key={i} className="flex items-start gap-3"><CheckCircle2 size={20} className="text-[#25D366] shrink-0 mt-0.5" /><span className="font-body text-[#0B1533]">{b}</span></div>
          ))}
        </div>
        <p className="font-body text-[#3A4058] leading-relaxed mb-10">{t.body(data)}</p>

        <h2 className="font-heading font-bold text-2xl text-[#0B1533] mb-5">{t.variantsT}</h2>
        <div className="flex flex-wrap gap-2 mb-10" data-testid="seo-variants">
          {variantKeywords(lang, data.city).map((k, i) => (
            <span key={i} className="font-body text-xs bg-[#F5F2ED] text-[#3A4058] rounded-full px-3 py-1.5">{k}</span>
          ))}
        </div>

        <h2 className="font-heading font-bold text-2xl text-[#0B1533] mb-5">{t.faqT}</h2>
        <div className="space-y-3 mb-10">
          {t.faq(data).map(([q, a], i) => (
            <div key={i} className="rounded-2xl border border-[#E9E5DE] p-5">
              <p className="font-heading font-semibold text-[#0B1533] mb-1.5">{q}</p>
              <p className="font-body text-sm text-[#5A607A] leading-relaxed">{a}</p>
            </div>
          ))}
        </div>

        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-[#0B1533] rounded-3xl p-7">
          <div className="flex items-center gap-3"><Plane size={26} className="text-[#C9A24B]" /><p className="font-heading font-semibold text-white text-lg">{canon.keyword}</p></div>
          <div className="flex items-center gap-3">
            <a href={BRAND.phoneHref} className="inline-flex items-center gap-2 border border-white/30 text-white rounded-full px-5 py-3 font-body font-semibold hover:bg-white/10 transition-colors"><Phone size={16} /> {t.call}</a>
            <button onClick={goBook} data-testid="seo-cta-bottom" className="inline-flex items-center gap-2 bg-[#2B4BFF] text-white rounded-full px-6 py-3 font-body font-semibold hover:bg-[#1E38D8] transition-colors">{t.cta} <ArrowRight size={16} /></button>
          </div>
        </div>

        <div className="mt-10">
          <h2 className="font-heading font-bold text-2xl text-[#0B1533] mb-5">{t.moreT}</h2>
          <div className="flex flex-wrap gap-2" data-testid="seo-more">
            {otherCities(data.city, 12).map((c, i) => (
              <Link key={i} to={`/${lang}/${buildPage(lang, "transfer", c).slug}`} className="font-body text-sm bg-white border border-[#E9E5DE] rounded-full px-4 py-2 text-[#0B1533] hover:border-[#2B4BFF] hover:text-[#2B4BFF] transition-colors">{cityName(c, lang)}</Link>
            ))}
          </div>
        </div>

        <div className="mt-8 text-center">
          <Link to={`/${lang}`} className="font-body text-sm text-[#2B4BFF] hover:underline">← {BRAND.name}</Link>
        </div>
      </section>
    </div>
  );
};

export default Landing;
